import React, { useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import {
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  FormHelperText,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'

import { PrizeCategory } from 'consts/prize'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import CustomTextField from 'components/CustomTextField'
import { ConfigUtils } from 'utils/config'

import { useStyles } from './styles'

interface PartnerIntegrationProps {
  storesEnabled: boolean
  setStoresEnabled: (value: boolean) => void
  tucanEnabled: boolean
  setTucanEnabled: (value: boolean) => void
  toteatEnabled: boolean
  setToteatEnabled: (value: boolean) => void
  tucanCategory?: PrizeCategory
  setTucanCategory: (category: PrizeCategory) => void
  toteatCategory?: PrizeCategory
  setToteatCategory: (category: PrizeCategory) => void
  currentSku: string
  setCurrentSku: (sku: string) => void
  skusList: string[]
  setSkusList: (skus: string[]) => void
  toteatBenefitId: string
  setToteatBenefitId: (id: string) => void
  errors?: {
    category?: {
      hasError: boolean
      message: string
    }
    skusList?: {
      hasError: boolean
      message: string
    }
    toteatBenefitId?: {
      hasError: boolean
      message: string
    }
  }
  isLoading?: boolean
  onValidationChange?: (hasErrors: boolean) => void
  hideTitle?: boolean
  disabled?: boolean
  showErrors?: boolean
}

const categories = Object.values(PrizeCategory)

export const PartnerIntegration: React.FC<PartnerIntegrationProps> = ({
  storesEnabled,
  setStoresEnabled,
  tucanEnabled,
  setTucanEnabled,
  toteatEnabled,
  setToteatEnabled,
  tucanCategory,
  setTucanCategory,
  toteatCategory,
  setToteatCategory,
  currentSku,
  setCurrentSku,
  skusList,
  setSkusList,
  toteatBenefitId,
  setToteatBenefitId,
  errors = {},
  isLoading = false,
  onValidationChange,
  hideTitle = false,
  disabled = false,
  showErrors = true,
}) => {
  const classes = useStyles()
  const { integrations } = ConfigUtils
  const isTucanEnabled = integrations.tucan.enabled
  const isToteatEnabled = integrations.toteat.enabled
  const hasAnyIntegration = isTucanEnabled || isToteatEnabled

  // Validaciones según los requerimientos
  const isToteatCategoryRequired = toteatEnabled
  const isTucanCategoryRequired = tucanEnabled
  const isSkuRequired = toteatEnabled && toteatCategory === PrizeCategory.General
  const isToteatBenefitRequired = toteatEnabled && toteatCategory === PrizeCategory.Checkout

  let toteatCategoryError
  if (showErrors) {
    if (isToteatCategoryRequired && !toteatCategory) {
      toteatCategoryError = { hasError: true, message: 'La categoría es obligatoria' }
    } else if (errors?.category) {
      toteatCategoryError = errors.category
    }
  }

  let tucanCategoryError
  if (showErrors) {
    if (isTucanCategoryRequired && !tucanCategory) {
      tucanCategoryError = { hasError: true, message: 'La categoría es obligatoria' }
    } else if (errors?.category) {
      tucanCategoryError = errors.category
    }
  }

  let skusError
  if (showErrors) {
    if (isSkuRequired && skusList.length === 0) {
      skusError = { hasError: true, message: 'El SKU es requerido' }
    } else if (errors?.skusList) {
      skusError = errors.skusList
    }
  }

  let benefitError
  if (showErrors) {
    if (isToteatBenefitRequired && !toteatBenefitId.trim()) {
      benefitError = { hasError: true, message: 'El ID de beneficio POS es obligatorio' }
    } else if (errors?.toteatBenefitId) {
      benefitError = errors.toteatBenefitId
    }
  }

  useEffect(() => {
    if (onValidationChange) {
      const hasErrors =
        (isTucanCategoryRequired && !tucanCategory) ||
        (isToteatCategoryRequired && !toteatCategory) ||
        (isSkuRequired && skusList.length === 0) ||
        (isToteatBenefitRequired && !toteatBenefitId.trim())
      onValidationChange(hasErrors)
    }
  }, [
    tucanCategory,
    toteatCategory,
    skusList,
    toteatBenefitId,
    isTucanCategoryRequired,
    isToteatCategoryRequired,
    isSkuRequired,
    isToteatBenefitRequired,
    onValidationChange,
  ])

  const handleCurrentSkuChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setCurrentSku(e.target.value)
  }

  const handleAddSku = () => {
    const trimmedSku = currentSku.trim()
    if (trimmedSku && !skusList.includes(trimmedSku)) {
      setSkusList([...skusList, trimmedSku])
      setCurrentSku('')
    }
  }

  const handleRemoveSku = (skuToRemove: string) => {
    setSkusList(skusList.filter(sku => sku !== skuToRemove))
  }

  const handleToteatBenefitIdChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setToteatBenefitId(e.target.value)
  }

  const handleTucanCategoryChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setTucanCategory(e.target.value as PrizeCategory)
  }

  const handleToteatCategoryChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setToteatCategory(e.target.value as PrizeCategory)
  }

  return (
    <>
      {!hideTitle && (
        <Grid item xs={11}>
          <Typography variant="h6">Integración con Partner</Typography>
        </Grid>
      )}

      {hasAnyIntegration && (
        <Grid item xs={12} alignContent="flex-end">
          <CustomFormControlLabel
            control={
              <CustomCheckbox
                onChange={e => setStoresEnabled(e.target.checked)}
                checked={storesEnabled}
                disabled={disabled}
              />
            }
            label="Disponible en locales"
          />
        </Grid>
      )}

      {isTucanEnabled && (
        <Grid container item xs={12}>
          <Grid item xs={6} alignContent="flex-end">
            <CustomFormControlLabel
              control={
                <CustomCheckbox
                  onChange={e => setTucanEnabled(e.target.checked)}
                  checked={tucanEnabled}
                  disabled={disabled}
                />
              }
              label="Disponible en Tucan"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl className={classes.fullWidth} error={tucanCategoryError?.hasError}>
              <InputLabel className={classes.inputLabel}>Categoría</InputLabel>
              <Select
                className={classes.inputLabel}
                value={tucanCategory}
                onChange={handleTucanCategoryChange}
                required={isTucanCategoryRequired}
                disabled={disabled}
              >
                {categories.map(option => (
                  <MenuItem className={classes.menuItem} key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {tucanCategoryError?.hasError && (
                <FormHelperText className={classes.inputLabelText}>
                  {tucanCategoryError.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      )}

      {isToteatEnabled && (
        <Grid container item xs={12}>
          <Grid item xs={6} alignContent="flex-end">
            <CustomFormControlLabel
              control={
                <CustomCheckbox
                  onChange={e => setToteatEnabled(e.target.checked)}
                  checked={toteatEnabled}
                  disabled={disabled}
                />
              }
              label="Disponible en POS"
            />
            <FormHelperText>Incluye Toteat, Ayres y otros POS</FormHelperText>
          </Grid>

          <Grid item xs={6}>
            <FormControl className={classes.fullWidth} error={toteatCategoryError?.hasError}>
              <InputLabel className={classes.inputLabel}>Categoría</InputLabel>
              <Select
                className={classes.inputLabel}
                value={toteatCategory}
                onChange={handleToteatCategoryChange}
                required={isToteatCategoryRequired}
                disabled={disabled}
              >
                {categories.map(option => (
                  <MenuItem className={classes.menuItem} key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {toteatCategoryError?.hasError && (
                <FormHelperText className={classes.inputLabelText}>
                  {toteatCategoryError.message}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      )}

      {isToteatEnabled && (
        <>
          {/* ID de beneficio POS - Solo visible para tipo Checkout */}
          {toteatCategory === PrizeCategory.Checkout && (
            <Grid item xs={12}>
              <CustomTextField
                label="ID de beneficio POS"
                value={toteatBenefitId}
                fullWidth
                required={isToteatBenefitRequired}
                maxLength={50}
                error={benefitError?.hasError}
                helperText={benefitError?.hasError ? benefitError.message : undefined}
                onChange={handleToteatBenefitIdChange}
                disabled={isLoading || disabled}
              />
            </Grid>
          )}
          {/* SKU - Solo visible para tipo General */}
          {toteatCategory === PrizeCategory.General && (
            <>
              <Grid item xs={6}>
                <Grid container spacing={2}>
                  <Grid item xs={9}>
                    <CustomTextField
                      label="SKU"
                      value={currentSku}
                      fullWidth
                      required={isSkuRequired}
                      error={skusError?.hasError}
                      helperText={skusError?.message}
                      onChange={handleCurrentSkuChange}
                      disabled={isLoading || disabled}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddSku()
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <IconButton
                      size="medium"
                      onClick={handleAddSku}
                      disabled={!currentSku.trim() || isLoading || disabled}
                    >
                      <AddIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <List>
                  {skusList.map(sku => (
                    <ListItem key={sku}>
                      <ListItemText className={classes.listItemText} disableTypography>
                        {sku}
                      </ListItemText>
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => handleRemoveSku(sku)} disabled={disabled}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </>
          )}
        </>
      )}
    </>
  )
}

PartnerIntegration.defaultProps = {
  errors: {},
  isLoading: false,
  onValidationChange: undefined,
  hideTitle: false,
  tucanCategory: undefined,
  toteatCategory: undefined,
  disabled: false,
  showErrors: true,
}

export default PartnerIntegration
